import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import {
	getCurrentUser,
	setCurrentUser,
	removeCurrentUser,
	removeUserDeviceInfo,
} from 'helpers/storage'
import { removeUserInfo, setUserInfo } from 'hooks/userInfo'
import store from '../hooks/store'
import Swal from 'sweetalert2'
import { removeCmmnCd } from 'hooks/cmmnCodeFactory'
import { removeDeviceInfo } from 'hooks/deviceInfo'

const instance = axios.create({
	baseURL: process.env.REACT_APP_HOST,
	timeout: 20000,
})

export default instance

instance.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		const userTokenInfo = getCurrentUser()
		if (userTokenInfo) {
			config.headers?.set(
				'Authorization',
				`${userTokenInfo.tokenType}${userTokenInfo.accessToken}`
			)
		}

		// FormData인지 체크 (multipart/form-data 요청)
		const isFormData = config.data instanceof FormData

		// FormData가 아닌 경우에만 application/json 헤더 설정
		// FormData인 경우 브라우저가 자동으로 multipart/form-data 헤더와 boundary 설정
		if (!isFormData) {
			config.headers?.set('Content-Type', 'application/json;charset=UTF-8')
		} else {
			// FormData인 경우 Content-Type 헤더를 삭제하여 브라우저가 자동 설정하도록 함
			config.headers?.delete('Content-Type')
		}

		return config
	},
	(error) => {
		return Promise.reject(error)
	}
)

instance.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error) => {
		const originalRequest = error.config
		if (
			error.response &&
			// eslint-disable-next-line no-underscore-dangle
			!originalRequest._retry &&
			error.response.status === 400
		) {
			// badrequest
			Swal.fire({
				title: 'Error!',
				text: error?.response?.data?.data,
				icon: 'error',
				confirmButtonText: '확인',
			}).then((result) => {
				return
			})
			return Promise.reject(error)
		}
		if (
			error.response &&
			// eslint-disable-next-line no-underscore-dangle
			!originalRequest._retry &&
			(error.response.status === 406 || error.response.status === 401)
		) {
			// eslint-disable-next-line no-underscore-dangle
			originalRequest._retry = true
			// const dispatch = useDispatch()
			const userTokenInfo = JSON.parse(
				localStorage.getItem(`${process.env.REACT_APP_USER_STORAGE_NAME}`) || ''
			)
			const responseData = await axios
				.post(`${process.env.REACT_APP_HOST}/auth/refresh`, {
					refreshToken: `${userTokenInfo.refreshToken}`,
				})
				.then((res) => res.data)
				.catch((err) => {
					Swal.fire({
						title: 'Error!',
						html:
							'토큰에 문제가 발생했습니다.<br />오류가 지속될 경우 관리자에게 문의하세요.',
						icon: 'error',
						confirmButtonText: '확인',
					}).then((result) => {
						if (result.isConfirmed) {
							removeCurrentUser()
							removeUserDeviceInfo()
							store.dispatch(removeDeviceInfo())
							store.dispatch(removeUserInfo())
							store.dispatch(removeCmmnCd())
							window.location.href = '/'
						}
					})
				})
			if (responseData) {
				const meResult = await axios
					.get(`${process.env.REACT_APP_HOST}/user/me`, {
						headers: {
							Authorization: `${responseData.tokenType}${responseData.accessToken}`,
						},
					})
					.then((res) => {
						const { data } = res
						setCurrentUser(responseData)
						store.dispatch(setUserInfo(data))
						instance.defaults.headers[
							'Authorization'
						] = `${responseData.tokenType}${responseData.accessToken}`
						return true
					})
					.catch((error) => {
						Swal.fire({
							title: 'Error!',
							text: error.response.data.data,
							icon: 'error',
							confirmButtonText: '확인',
						})
					})
				if (meResult) {
					return instance(originalRequest)
				}
			} else {
				// 토큰 갱신 실패
				Swal.fire({
					title: 'Error!',
					html:
						'토큰 갱신에 실패했습니다.<br />문제가 지속될 경우 관리자에게 문의하세요.',
					icon: 'error',
					confirmButtonText: '확인',
				}).then((result) => {
					if (result.isConfirmed) {
						removeCurrentUser()
						removeUserDeviceInfo()
						store.dispatch(removeDeviceInfo())
						store.dispatch(removeUserInfo())
						store.dispatch(removeCmmnCd())
						window.location.href = '/'
					}
				})
			}
		} else if (error?.response?.status === 500) {
			Swal.fire({
				title: 'Error!',
				text: error.response.data.message,
				icon: 'error',
				confirmButtonText: '확인',
			})
		} else if (error?.response?.status === 403) {
			// api exception
			console.log('403 error', error)
			Swal.fire({
				title: 'Error!',
				text: '권한이 없습니다.',
				icon: 'error',
				confirmButtonText: '확인',
			})
		} else {
			// 토큰 경과시 response가 안나와서 임시 조치... 추후 수정해야함
			console.log(originalRequest._retry)
			if (!originalRequest._retry) {
				// eslint-disable-next-line no-underscore-dangle
				originalRequest._retry = true
				// const dispatch = useDispatch()
				const userTokenInfo = JSON.parse(
					localStorage.getItem(`${process.env.REACT_APP_USER_STORAGE_NAME}`) || ''
				)
				const responseData = await axios
					.post(`${process.env.REACT_APP_HOST}/auth/refresh`, {
						refreshToken: `${userTokenInfo.refreshToken}`,
					})
					.then((res) => res.data)
					.catch((err) => {
						Swal.fire({
							title: 'Error!',
							html:
								'토큰에 문제가 발생했습니다.<br />오류가 지속될 경우 관리자에게 문의하세요.',
							icon: 'error',
							confirmButtonText: '확인',
						}).then((result) => {
							if (result.isConfirmed) {
								removeCurrentUser()
								removeUserDeviceInfo()
								store.dispatch(removeDeviceInfo())
								store.dispatch(removeUserInfo())
								store.dispatch(removeCmmnCd())
								window.location.href = '/'
							}
						})
					})
				if (responseData) {
					const meResult = await axios
						.get(`${process.env.REACT_APP_HOST}/user/me`, {
							headers: {
								Authorization: `${responseData.tokenType}${responseData.accessToken}`,
							},
						})
						.then((res) => {
							const { data } = res
							setCurrentUser(responseData)
							store.dispatch(setUserInfo(data))
							instance.defaults.headers[
								'Authorization'
							] = `${responseData.tokenType}${responseData.accessToken}`
							return true
						})
						.catch((error) => {
							Swal.fire({
								title: 'Error!',
								text: error.response.data.data,
								icon: 'error',
								confirmButtonText: '확인',
							})
						})
					if (meResult) {
						return instance(originalRequest)
					}
				} else {
					// 토큰 갱신 실패
					Swal.fire({
						title: 'Error!',
						html:
							'토큰 갱신에 실패했습니다.<br />문제가 지속될 경우 관리자에게 문의하세요.',
						icon: 'error',
						confirmButtonText: '확인',
					}).then((result) => {
						if (result.isConfirmed) {
							removeCurrentUser()
							removeUserDeviceInfo()
							store.dispatch(removeDeviceInfo())
							store.dispatch(removeUserInfo())
							store.dispatch(removeCmmnCd())
							window.location.href = '/'
						}
					})
				}
			} else {
				// api exception
				let errorMessage = error.message
				// 서버 접속 불가
				if (error.code === 'ERR_NETWORK') {
					errorMessage = '서버에 접속할 수 없습니다.<br />잠시 후 다시 시도해주세요.'
				} else if (error.code === 'ECONNABORTED') {
					// 서버 응답 지연
					errorMessage =
						'서버 응답이 지연되고 있습니다.<br />잠시 후 다시 시도해주세요.'
				}
				Swal.fire({
					title: 'Error!',
					html: errorMessage,
					icon: 'error',
					confirmButtonText: '확인',
				})
			}
		}
		return Promise.reject(error)
	}
)
